import React from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { SmsOptInCard } from "@/components/SmsOptInCard";
import { useSEO } from "@/lib/seo";
import { ArrowRight, PenLine, MessageSquareText, ShieldCheck } from "lucide-react";

const ACTS = [
  {
    n: "01", Icon: PenLine, title: "Log it, effortlessly",
    body: "Enter the client, amount, date and terms — or log a scheduled platform payout instead. About fifteen seconds, no categories to learn. The record lands in your ledger and starts watching its own dates.",
  },
  {
    n: "02", Icon: MessageSquareText, title: "Optional, quiet text alerts",
    body: "If you'd like a nudge, confirm your number once. Reply YES and transactional alerts begin; reply STOP and nothing further is ever sent. Silence is the default — you opt in, never the other way around.",
  },
  {
    n: "03", Icon: ShieldCheck, title: "Peace of mind, in your timezone",
    body: "PaySignal reads each record as Upcoming, Due Today, or Overdue in your local time — and can text you the day it changes, or when you mark it paid. You stop keeping the calendar in your head.",
  },
];

const OPTIN = [
  "You add your number and turn on the SMS consent toggle. It is off by default and separate from accepting the Terms. We record the timestamp, the page, and the exact consent wording you agreed to.",
  "We send one confirmation text. Nothing else is sent until you answer it.",
  "You reply YES. That reply is stored with its own timestamp — this is the double opt-in.",
  "Alerts begin. Only the message types listed on this page are ever sent, and only about your own account.",
  "You reply STOP whenever you want. Your number is suppressed and excluded from all future sending.",
];

const MESSAGES = [
  { type: "Double opt-in request", text: "PaySignal: Reply YES to confirm account notifications. Reply STOP to decline. Msg & data rates may apply." },
  { type: "Confirmation (after YES)", text: "PaySignal: You're confirmed. We'll text account notifications about activity in your account. Reply STOP to opt out, HELP for help." },
  { type: "Invoice due today", text: "PaySignal: INV-3814 for North Coast Studio, $1,260.00, is due today. Reply STOP to opt out." },
  { type: "Invoice overdue", text: "PaySignal: INV-3798 for Cedar & Fifth, $1,450.00, is now 12 days past due. Reply STOP to opt out, HELP for help." },
  { type: "Payout received", text: "PaySignal: You marked PAY-1390 ($950.00, Design Marketplace) as received. 2026 total is now $19,370. Reply STOP to opt out." },
  { type: "STOP auto-response", text: "PaySignal: You are unsubscribed and will receive no further texts. Reply START to resubscribe." },
  { type: "HELP auto-response", text: "PaySignal: Account alerts for invoices & payouts. Support: contact@paysignal.ai. Reply STOP to opt out." },
];

const KEYWORDS = [
  { k: "STOP", a: "Opt-out confirmation sent. All further messages stop immediately." },
  { k: "CANCEL", a: "Same as STOP." },
  { k: "END", a: "Same as STOP." },
  { k: "QUIT", a: "Same as STOP." },
  { k: "UNSUBSCRIBE", a: "Same as STOP." },
  { k: "HELP", a: "Help response with brand name, support email and STOP instructions." },
  { k: "INFO", a: "Same as HELP." },
  { k: "START", a: "Re-subscribe confirmation. Sending resumes." },
  { k: "YES", a: "Confirms the double opt-in. Program messages begin." },
];

export default function HowItWorks() {
  useSEO({
    title: "How PaySignal Works — Invoices, Payouts & Text Alerts",
    description: "See exactly how PaySignal tracks invoices and payouts, how the optional SMS opt-in works, every message we can send, and how STOP and HELP are handled.",
    canonical: window.location.origin + "/how-it-works",
  });

  return (
    <div className="min-h-screen bg-cream text-espresso">
      <SiteHeader />

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-20 pb-12 text-center">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#8C6D58]">
          <span className="w-6 h-px bg-[#8C6D58]" /> How it works
        </span>
        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 text-espresso leading-[1.08]" data-testid="how-title">
          Log it, and PaySignal <em className="italic text-olive">watches the dates</em> for you.
        </h1>
        <p className="text-lg text-espresso-soft mt-6 leading-relaxed">
          A calm ledger plus optional, transparent text alerts. Here's exactly what happens — and every message we could ever send.
        </p>
      </section>

      {/* Three acts */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="space-y-10">
          {ACTS.map(({ n, Icon, title, body }, i) => (
            <div key={n} data-testid={`how-step-${i + 1}`}
              className="grid md:grid-cols-12 gap-6 items-start">
              <div className="md:col-span-3 flex items-center gap-4">
                <span className="font-serif text-5xl lg:text-6xl font-light text-olive/30 leading-none">{n}</span>
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-olive/10 text-olive shrink-0"><Icon size={22} /></span>
              </div>
              <div className="md:col-span-9 md:border-l border-warmborder md:pl-8">
                <h3 className="font-heading text-xl sm:text-2xl font-semibold text-espresso">{title}</h3>
                <p className="text-base text-espresso-soft mt-3 leading-relaxed max-w-2xl">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Opt-in card showcase */}
      <section className="bg-olive-light border-y border-warmborder">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-olive">Turn it on, on your terms</span>
          <h2 className="font-serif font-normal text-3xl sm:text-4xl tracking-tight text-espresso mt-4 leading-tight">A single, transparent step</h2>
          <p className="text-base text-espresso-soft mt-4 max-w-xl mx-auto leading-relaxed">
            This is the opt-in you'll see inside PaySignal. Off by default, explicit consent, full disclosure right where you tap.
          </p>
          <div className="mt-10"><SmsOptInCard /></div>
        </div>
      </section>

      {/* Opt-in flow */}
      <section className="max-w-3xl mx-auto px-6 py-20">
        <h2 className="font-serif font-normal text-3xl sm:text-4xl tracking-tight text-espresso">The opt-in flow, step by step</h2>
        <p className="text-espresso-soft mt-3">Text alerts are optional and use a double opt-in. You are always in control.</p>
        <ol className="mt-8 space-y-4">
          {OPTIN.map((s, i) => (
            <li key={i} className="flex gap-4 border border-warmborder rounded-2xl bg-white p-5" data-testid={`optin-${i + 1}`}>
              <span className="grid place-items-center w-8 h-8 rounded-full bg-espresso text-cream text-sm font-bold shrink-0">{i + 1}</span>
              <p className="text-espresso-soft leading-relaxed">{s}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Every message */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <h2 className="font-serif font-normal text-3xl sm:text-4xl tracking-tight text-espresso">Every message in the program</h2>
        <p className="text-espresso-soft mt-3">These are the only texts PaySignal sends. All transactional, never marketing.</p>
        <div className="mt-6 space-y-3">
          {MESSAGES.map((m) => (
            <div key={m.type} className="border border-warmborder rounded-2xl bg-white p-4" data-testid={`msg-${m.type}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-olive">{m.type}</p>
              <p className="text-sm text-espresso mt-1.5 leading-relaxed bg-olive-light rounded-xl px-3.5 py-2.5">{m.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Keywords */}
      <section className="max-w-3xl mx-auto px-6 pb-20">
        <h2 className="font-serif font-normal text-3xl sm:text-4xl tracking-tight text-espresso">Keyword responses</h2>
        <div className="mt-6 border border-warmborder rounded-2xl bg-white overflow-hidden divide-y divide-warmborder">
          {KEYWORDS.map((row) => (
            <div key={row.k} className="flex items-start gap-4 px-5 py-3.5">
              <span className="font-num font-bold text-sm text-espresso bg-oatmeal rounded px-2 py-0.5 shrink-0 min-w-[92px] text-center">{row.k}</span>
              <span className="text-sm text-espresso-soft">{row.a}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-warmmuted mt-4">A global STOP always overrides individual alert preferences. Message and data rates may apply.</p>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="bg-espresso rounded-[2rem] p-10 sm:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-olive/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-12 w-64 h-64 rounded-full bg-olive/25 blur-3xl pointer-events-none" />
          <h2 className="relative font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-cream leading-tight">Ready to see what's coming?</h2>
          <p className="relative text-cream/70 mt-4 max-w-md mx-auto leading-relaxed">Set up your ledger in under a minute. Alerts are optional, always.</p>
          <Link to="/signup"><Button size="lg" className="relative mt-8 rounded-full px-8 h-12 bg-lime text-espresso hover:bg-lime-dark hover:text-espresso" data-testid="how-cta">Start tracking <ArrowRight size={18} className="ml-1.5" /></Button></Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
